import type { Interceptor } from '@connectrpc/connect'
import { TransportProvider, useTransport } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import React, { createElement } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { sessionTokenInterceptor } from '@/lib/auth/session-token-interceptor'

export const getConnectTransport = ({ interceptors = [] }: { interceptors?: Interceptor[] }) =>
  createConnectTransport({
    baseUrl: '/api',
    interceptors: interceptors,
    useHttpGet: false,
    defaultTimeoutMs: 60000, // 1 minute timeout so the BE (envoy) can decide the timeout
  })

export const defaultTransport = getConnectTransport({
  interceptors: [sessionTokenInterceptor],
})

export const RecipientQuestionnaireTransportProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { portalId } = useParams<{ portalId: string }>()
  const navigate = useNavigate()

  const transport = React.useMemo(
    () =>
      getConnectTransport({
        interceptors: [
          sessionTokenInterceptor,
          (next) => async (req) => {
            // TODO: Find a better way to handle redirects
            try {
              const res = await next(req)

              // Check for redirect responses with Location header
              const locationHeader = res.header.get('Location')

              if (locationHeader) {
                navigate(locationHeader)
              }

              return res
            } catch (error) {
              // Check if this is a ConnectError with redirect information
              if (error && typeof error === 'object' && 'metadata' in error) {
                const errorWithMetadata = error as { metadata: { get?: (key: string) => string } }
                const locationHeader =
                  errorWithMetadata.metadata?.get?.('Location') ||
                  errorWithMetadata.metadata?.get?.('location')

                if (locationHeader) {
                  navigate(locationHeader)
                }
              }

              // Always re-throw the error to maintain the Connect error flow
              throw error
            }
          },
          (next) => async (req) => {
            if (portalId) {
              req.header.set('x-portal-org-company-id', portalId)
            }
            return next(req)
          },
        ],
      }),
    [portalId, navigate],
  )

  return createElement(TransportProvider, { transport }, children)
}

// Hook to get the transport from Connect Query's context
export const useGetRecipientQuestionnaireTransport = () => {
  return useTransport()
}
